/* project-lifeline.jsx — dashboard event and progress primitives for Project
   Detail. Pure presentation over the live snapshot `db` + `project`.
   Exports: window.EventOverview, window.ProgressRing. */
(function () {
  const { money, fmtDate } = window.AC;
  const MODEL = window.MODEL;
  const { useMemo, useState } = React;

  const iso = (v) => String(v).slice(0, 10);
  const parse = (v) => { const [y, m, d] = iso(v).split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const toIso = (ms) => new Date(ms).toISOString().slice(0, 10);
  const addMonths = (ms, n) => { const d = new Date(ms); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, d.getUTCDate()); };
  const addDays = (ms, n) => ms + n * 86400000;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const TYPE_STYLE = {
    meeting: { label: 'Meeting', icon: 'calendar', accent: 'var(--primary)', soft: 'var(--primary-soft)' },
    milestone: { label: 'Milestone', icon: 'flag', accent: 'var(--pos)', soft: 'var(--pos-soft)' },
    billing: { label: 'Billing', icon: 'refresh', accent: 'var(--info)', soft: '#e2f1f4' },
    invoice: { label: 'Invoice', icon: 'invoices', accent: 'var(--warn)', soft: 'var(--warn-soft)' },
  };
  const MILESTONE_STYLE = {
    done: { accent: 'var(--pos)', soft: 'var(--pos-soft)' },
    due: { accent: 'var(--warn)', soft: 'var(--warn-soft)' },
    overdue: { accent: 'var(--neg)', soft: 'var(--neg-soft)' },
  };
  const EVENT_OVERVIEW_DAY_COUNT = 7;

  function milestoneState(milestone) {
    if (milestone.completed) return 'done';
    return milestone.dueDate && iso(milestone.dueDate) < MODEL.TODAY ? 'overdue' : 'due';
  }

  function billingDates(del, fromMs, toMs) {
    const start = parse(del.billingStartDate || MODEL.TODAY);
    const freq = del.recurrenceFrequency || 'None';
    const end = del.recurrenceEndDate ? Math.min(parse(del.recurrenceEndDate), toMs) : toMs;
    if (freq === 'None') return start >= fromMs && start <= toMs ? [toIso(start)] : [];
    const out = [];
    let cur = start;
    let guard = 0;
    while (cur <= end && guard < 120) {
      if (cur >= fromMs) out.push(toIso(cur));
      cur = freq === 'Weekly' ? addDays(cur, 7) : freq === 'Yearly' ? addMonths(cur, 12) : addMonths(cur, 1);
      guard++;
    }
    return out;
  }

  function buildProjectEvents(project, db, horizonDays = EVENT_OVERVIEW_DAY_COUNT - 1) {
    const ev = [];
    const todayMs = parse(MODEL.TODAY);
    const endMs = addDays(todayMs, horizonDays);

    (project.milestones || []).forEach((m) => {
      if (!m.dueDate) return;
      const state = milestoneState(m);
      ev.push({
        date: iso(m.dueDate),
        type: 'milestone',
        milestoneState: state,
        title: m.title || 'Milestone',
        sub: m.completed ? 'Completed milestone' : state === 'overdue' ? 'Overdue milestone' : 'Milestone due',
        order: '12:00',
      });
    });

    (db.calendarEvents || []).forEach((event) => {
      if (event.projectId !== project.id) return;
      const meeting = window.CAL ? window.CAL.toMeeting(event) : {
        title: event.title || 'Meeting',
        type: event.type || 'Meeting',
        status: event.status || 'Scheduled',
        date: event.startsAt || MODEL.TODAY,
        start: event.startsAt ? new Date(event.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
      };
      const status = meeting.status || 'Scheduled';
      if (/cancell?ed/i.test(status)) return;
      ev.push({
        date: iso(meeting.date || event.startsAt || MODEL.TODAY),
        type: 'meeting',
        title: meeting.title || 'Meeting',
        sub: `${meeting.type || 'Meeting'}${meeting.start ? ' · ' + meeting.start : ''}${status ? ' · ' + status : ''}`,
        time: meeting.start || '',
        order: meeting.start || '09:00',
      });
    });

    (project.projectDeliverables || []).forEach((d) => {
      if (d.freeOfCharge) return;
      billingDates(d, todayMs, endMs).forEach((date) => ev.push({
        date,
        type: 'billing',
        title: d.name || 'Deliverable billing',
        sub: (d.recurrenceFrequency && d.recurrenceFrequency !== 'None') ? d.recurrenceFrequency + ' billing' : 'One-off billing',
        amount: d.unitPrice,
        amountLabel: money(d.unitPrice || 0),
        order: '15:00',
      }));
    });

    (db.invoices || []).forEach((iv) => {
      const lines = iv.lines || [];
      const linked = lines.some((l) => l.projectId === project.id || (project.projectDeliverables || []).some((d) => d.id === l.projectDeliverableId));
      if (!linked) return;
      const e = MODEL.enrich(db, iv);
      const date = e.open ? (iv.dueDate || iv.issueDate) : iv.issueDate;
      ev.push({
        date: iso(date),
        type: 'invoice',
        title: iv.number || 'Invoice',
        sub: `Invoice · ${e.display}${iv.dueDate ? ' · due ' + fmtDate(iv.dueDate) : ''}`,
        amount: e.totals.total,
        amountLabel: money(e.totals.total),
        order: '16:00',
      });
    });

    return ev
      .filter((event) => {
        const eventMs = parse(event.date);
        return eventMs >= todayMs && eventMs <= endMs;
      })
      .sort((a, b) => a.date.localeCompare(b.date) || String(a.order || '').localeCompare(String(b.order || '')) || a.title.localeCompare(b.title));
  }

  function dayLabel(value) {
    const d = new Date(parse(value));
    return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  }

  function countLabel(count, single, plural) {
    return `${count} ${count === 1 ? single : plural}`;
  }

  function EventOverview({ project, db }) {
    const today = MODEL.TODAY;
    const [selectedDay, setSelectedDay] = useState(today);
    const events = useMemo(() => buildProjectEvents(project, db), [project, db]);
    const visibleStart = today;
    const visibleEnd = toIso(addDays(parse(today), EVENT_OVERVIEW_DAY_COUNT - 1));
    const dayCount = Math.round((parse(visibleEnd) - parse(visibleStart)) / 86400000) + 1;
    const days = useMemo(() => Array.from({ length: dayCount }, (_, index) => {
      const value = toIso(addDays(parse(visibleStart), index));
      const date = new Date(parse(value));
      const dayEvents = events.filter((event) => event.date === value);
      const types = new Set(dayEvents.map((event) => event.type));
      const milestoneStates = dayEvents.filter((event) => event.type === 'milestone').map((event) => event.milestoneState || 'due');
      const milestoneState = milestoneStates.includes('overdue') ? 'overdue' : milestoneStates.includes('due') ? 'due' : milestoneStates.includes('done') ? 'done' : '';
      return {
        value,
        label: WEEKDAYS[date.getUTCDay()].slice(0, 1),
        number: date.getUTCDate(),
        weekend: date.getUTCDay() === 0 || date.getUTCDay() === 6,
        today: value === today,
        selected: value === selectedDay,
        types,
        milestoneState,
      };
    }), [dayCount, events, selectedDay, today, visibleStart]);
    const counts = events.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, { meeting: 0, milestone: 0, billing: 0, invoice: 0 });
    const detailEvents = events.filter((event) => event.date === selectedDay);
    const detailLabel = `${dayLabel(selectedDay)}${selectedDay === today ? ' · Today' : ''}`;
    const rangeLabel = `${fmtDate(visibleStart)} - ${fmtDate(visibleEnd)}`;

    return (
      <Panel
        title="Event overview"
        sub={"Coming week · click a day to see what's on"}
        className="pd-event-panel"
        actions={
          <div className="pd-event-counts">
            <span className="pd-event-chip meeting"><i />{countLabel(counts.meeting, 'meeting', 'meetings')}</span>
            <span className="pd-event-chip milestone"><i />{countLabel(counts.milestone, 'milestone', 'milestones')}</span>
            <span className="pd-event-chip billing"><i />{countLabel(counts.billing, 'billing', 'billing')}</span>
            <span className="pd-event-chip invoice"><i />{countLabel(counts.invoice, 'invoice', 'invoices')}</span>
          </div>
        }
      >
        <div className="pd-event-range">{rangeLabel}</div>
        <div className={'pd-day-strip' + (dayCount > 31 ? ' extended' : '')}>
          {days.map((day) => (
            <button
              key={day.value}
              type="button"
              className={'pd-day-cell' + (day.weekend ? ' weekend' : '') + (day.today ? ' today' : '') + (day.selected ? ' selected' : '')}
              onClick={() => setSelectedDay(day.value)}
              title={fmtDate(day.value)}
            >
              <span className="wd">{day.label}</span>
              <span className="num">{day.number}</span>
              <span className="dots">
                {Object.keys(TYPE_STYLE).map((type) => {
                  if (!day.types.has(type)) return null;
                  return type === 'milestone'
                    ? <span key={type} className={`milestone-marker ${day.milestoneState}`}><Icon name="flag" size={8} stroke={2} /></span>
                    : <i key={type} className={type} />;
                })}
              </span>
            </button>
          ))}
        </div>

        <div className="pd-event-detail">
          <div className="pd-event-detail-head">
            <span className="pd-event-detail-icon"><Icon name="calendar" size={15} /></span>
            <div>
              <strong>{detailLabel}</strong>
              <span>{detailEvents.length} event{detailEvents.length === 1 ? '' : 's'} scheduled</span>
            </div>
          </div>
          {detailEvents.length ? detailEvents.map((event, index) => {
            const style = event.type === 'milestone'
              ? { ...TYPE_STYLE.milestone, ...(MILESTONE_STYLE[event.milestoneState] || MILESTONE_STYLE.due) }
              : (TYPE_STYLE[event.type] || TYPE_STYLE.meeting);
            return (
              <div className="pd-event-row" key={`${event.type}-${event.date}-${event.title}-${index}`}>
                <span className="pd-event-ico" style={{ background: style.soft, color: style.accent }}><Icon name={style.icon} size={15} /></span>
                <div>
                  <strong>{event.title}</strong>
                  <span>{event.sub}</span>
                </div>
                {event.amountLabel ? <b>{event.amountLabel}</b> : event.time ? <b className="muted">{event.time}</b> : null}
              </div>
            );
          }) : <div className="pd-event-empty"><Icon name="check" size={15} />Nothing scheduled - a clear day.</div>}
          <div className="pd-event-legend">
            {Object.entries(TYPE_STYLE).map(([type, style]) => <span key={type}><i className={type} />{style.label}</span>)}
          </div>
        </div>
      </Panel>
    );
  }

  function ProgressRing({ value, size = 96, stroke = 9, label, color = 'var(--pos)' }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(1, value));
    return (
      <div className="ring" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .4s' }} />
        </svg>
        <div className="ring-center">
          <span className="ring-pct" style={{ fontSize: size * 0.26 }}>{Math.round(pct * 100)}%</span>
          {label && <span className="ring-sub">{label}</span>}
        </div>
      </div>
    );
  }

  Object.assign(window, { EventOverview, ProgressRing });
})();
